import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, Activity, ShieldCheck, TrendingUp, Server, IndianRupee } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/business/PageHeader";

const SECTION_META = {
  quality:         { label: "Quality",         icon: ShieldCheck, tint: "text-emerald-600" },
  operational:     { label: "Operational",     icon: Activity,    tint: "text-sky-600" },
  security:        { label: "Security",        icon: ShieldCheck, tint: "text-rose-600" },
  platform_health: { label: "Platform Health", icon: Server,      tint: "text-indigo-600" },
  commercial:      { label: "Commercial",      icon: IndianRupee, tint: "text-amber-600" },
};

const STATUS_STYLE = {
  healthy:  "bg-emerald-50 text-emerald-700 border-emerald-200",
  warning:  "bg-amber-50 text-amber-700 border-amber-200",
  critical: "bg-rose-50 text-rose-700 border-rose-200",
};

const SOURCE_STYLE = {
  live:      "bg-emerald-50 text-emerald-700",
  estimated: "bg-amber-50 text-amber-700",
  mocked:    "bg-slate-100 text-slate-600",
};

function formatValue(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function MetricRow({ name, m }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-border/60 last:border-0" data-testid={`gov-metric-${name}`}>
      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium text-foreground/80 truncate">{name.replace(/_/g, " ")}</div>
        <div className="mt-0.5 font-display text-lg font-semibold num-rupee break-words">{formatValue(m.value)}</div>
        {m.note && <div className="mt-0.5 text-[11px] text-muted-foreground">{m.note}</div>}
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        <Badge variant="outline" className={STATUS_STYLE[m.status] || STATUS_STYLE.healthy}>
          {m.trend} {m.status}
        </Badge>
        <span className={`text-[10px] px-1.5 py-0.5 rounded ${SOURCE_STYLE[m.data_source] || SOURCE_STYLE.estimated}`}>
          {m.data_source}
        </span>
      </div>
    </div>
  );
}

function SectionCard({ sectionKey, metrics }) {
  const meta = SECTION_META[sectionKey] || { label: sectionKey, icon: Activity, tint: "text-foreground" };
  const Icon = meta.icon;
  return (
    <Card className="p-5 border-border" data-testid={`gov-section-${sectionKey}`}>
      <div className="flex items-center gap-2 pb-3 border-b border-border">
        <Icon className={`h-4 w-4 ${meta.tint}`} />
        <div className="font-display text-base font-semibold">{meta.label}</div>
        <div className="ml-auto text-xs text-muted-foreground">{Object.keys(metrics).length} metrics</div>
      </div>
      <div className="mt-2">
        {Object.entries(metrics).map(([k, m]) => <MetricRow key={k} name={k} m={m} />)}
      </div>
    </Card>
  );
}

export default function AdminGovernance() {
  const [snap, setSnap] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async (force = false) => {
    setBusy(true);
    try {
      const { data } = force
        ? await api.post("/admin/governance/refresh")
        : await api.get("/admin/governance");
      setSnap(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load governance snapshot");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (!snap) {
    return (
      <AppShell>
        <div className="max-w-7xl mx-auto p-6 text-sm text-muted-foreground">Loading governance snapshot…</div>
      </AppShell>
    );
  }

  const v = snap.version || {};
  const s = snap.summary || {};

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto">
        <PageHeader className="mb-2">
          <PageHeader.Title>Release Governance</PageHeader.Title>
          <PageHeader.Actions>
            <Button onClick={() => load(true)} disabled={busy} data-testid="gov-refresh-btn" className="gap-2">
              <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </PageHeader.Actions>
        </PageHeader>
        <p className="mb-4 text-sm text-muted-foreground">Live snapshot of platform health, testing, security and commercial signals.</p>

        {/* Meta strip */}
        <Card className="p-4 border-border mb-6" data-testid="gov-meta">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 text-xs">
            <MetaKV k="Release" v={v.release_tag} />
            <MetaKV k="Build" v={v.build_version} />
            <MetaKV k="API" v={v.api_version} />
            <MetaKV k="Schema" v={v.schema_version} />
            <MetaKV k="Rules" v={v.rules_engine_version} />
            <MetaKV k="AI Prompt" v={v.ai_prompt_version} />
            <MetaKV k="Env" v={v.environment} />
          </div>
          <div className="mt-3 pt-3 border-t border-border flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
            <span data-testid="gov-snapshot-at">Snapshot: {new Date(snap.snapshot_at).toLocaleString()}</span>
            <span>Refresh: {snap.refresh_duration_ms}ms</span>
            <span>
              Cache: <Badge variant="outline" className="ml-1">{snap.cache_status}</Badge>
            </span>
            <span>
              Service:
              <Badge variant="outline" className={`ml-1 ${STATUS_STYLE[snap.metrics_service_health]}`}>
                {snap.metrics_service_health}
              </Badge>
            </span>
            <span className="ml-auto">
              <b>{s.healthy}</b> healthy · <b>{s.warning}</b> warning · <b>{s.critical}</b> critical / {s.total_metrics}
            </span>
          </div>
        </Card>

        <div className="grid gap-4 lg:grid-cols-2">
          <SectionCard sectionKey="quality"         metrics={snap.quality || {}} />
          <SectionCard sectionKey="operational"     metrics={snap.operational || {}} />
          <SectionCard sectionKey="security"        metrics={snap.security || {}} />
          <SectionCard sectionKey="platform_health" metrics={snap.platform_health || {}} />
          <SectionCard sectionKey="commercial"      metrics={snap.commercial || {}} />
        </div>
      </div>
    </AppShell>
  );
}

function MetaKV({ k, v }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{k}</div>
      <div className="font-mono text-xs truncate" title={v}>{v || "—"}</div>
    </div>
  );
}
